import { useEffect, useMemo, useRef, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    LinearProgress,
    Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import { pdf } from "@react-pdf/renderer";
import { Question } from "../../../../model/questions-summary.model";
import { Candidate } from "../../../../model/candidate.model";
import { getAllCandidate, getReportById } from "../../../../services/api.service";
import {
    isCheckboxQuestion,
    isMultipleChoiceLike,
    MULTIPLE_CHOICE,
    MULTIPLE_CHOICE_NO_ANSWER,
} from "../../../../shared/question-type.util";
import GeneralQuestionnaireDashboardPDF, {
    DashboardPdfData,
    QuestionPdfRow,
} from "./GeneralQuestionnaireDashboardPDF";
import "./general-questionnaire-dashboard.component.scss";

interface Props {
    jobQuestionId: string | undefined;
    questions: Question[];
    /** Optional — used as the cover-page title in the downloaded PDF */
    jobTitle?: string;
    /** Optional — appended to the cover page next to the job title */
    companyName?: string;
}

interface ReportTranscriptLog {
    question_id?: string;
    question_type?: string;
    answer_type?: string;
    ui_variant?: string;
    response?:
        | {
              index?: string | number;
              value?: string;
              indexes?: Array<string | number>;
          }
        | string
        | number
        | null;
    choices?: Array<{ th?: string; en?: string }>;
}

interface ReportShape {
    report_id?: string;
    interview_status?: string;
    transcript?: {
        logs?: ReportTranscriptLog[];
        answers?: Array<{ question_id?: string; text?: string }>;
    };
}

interface PerQuestionAggregate {
    /** จำนวนผู้สมัครที่ตอบข้อนี้ (มีคำตอบหรือมีตัวเลือก) */
    answered: number;
    /** จำนวนผู้สมัครรวมที่ใช้คิด */
    totalEvaluated: number;
    /** สำหรับ multiple choice / no_answer / checkbox: counts ต่อ choice index */
    choiceCounts?: number[];
    /** ความถูกต้องเมื่อมีเฉลย */
    correctCount?: number;
    /** จำนวน vote รวมทั้งหมด (สำหรับ checkbox อาจ > answered เพราะคนเลือกได้หลายข้อ) */
    totalVotes?: number;
    /** สำหรับ voice / free_text */
    averageCharLength?: number;
    /** ตัวอย่างคำตอบล่าสุด (≤5) */
    answerSamples?: string[];
}

const MAX_SAMPLES = 5;

function parseResponseIndex(raw: unknown): number | null {
    if (raw === "" || raw === null || raw === undefined) return null;
    const n = Number(raw);
    if (!Number.isFinite(n) || n === 99) return null;
    return n;
}

/** answer index อาจเก็บเป็น log.response (number/string) หรือ log.response.index */
function readChoiceIndexFromLog(log: ReportTranscriptLog): number | null {
    const r = log?.response;
    if (r == null) return null;
    if (typeof r === "object") return parseResponseIndex(r.index);
    return parseResponseIndex(r);
}

/** สำหรับ checkbox: คืน array ของ index ที่ผู้สมัครเลือก (อาจเป็นช่องเดียวหรือหลายช่อง) */
function readChoiceIndexesFromLog(log: ReportTranscriptLog): number[] {
    const out: number[] = [];
    const r = log?.response;
    if (r == null) return out;
    if (typeof r === "object" && Array.isArray(r.indexes)) {
        for (const v of r.indexes) {
            const n = parseResponseIndex(v);
            if (n !== null && !out.includes(n)) out.push(n);
        }
    }
    if (out.length === 0) {
        const single = readChoiceIndexFromLog(log);
        if (single !== null) out.push(single);
    }
    return out.sort((a, b) => a - b);
}

function readFreeTextAnswer(
    log: ReportTranscriptLog,
    answers: ReportShape["transcript"] extends infer T
        ? T extends { answers?: infer A }
            ? A
            : never
        : never,
): string {
    const a = (answers as Array<{ question_id?: string; text?: string }> | undefined)?.find(
        (x) => x && String(x.question_id) === String(log.question_id),
    );
    if (a && typeof a.text === "string" && a.text.trim()) return a.text.trim();
    const r = log?.response;
    if (r == null) return "";
    if (typeof r === "object") return String(r.value ?? "").trim();
    return String(r).trim();
}

function choiceLabel(choice: { th?: string; en?: string } | undefined, idx: number): string {
    if (!choice) return `ตัวเลือก ${idx + 1}`;
    const th = (choice.th ?? "").trim();
    const en = (choice.en ?? "").trim();
    if (th && en) return `${th} (${en})`;
    return th || en || `ตัวเลือก ${idx + 1}`;
}

const PALETTE = [
    "#4A25E1",
    "#0ea5e9",
    "#10b981",
    "#f59e0b",
    "#ef4444",
    "#8b5cf6",
    "#14b8a6",
    "#ec4899",
];

const GeneralQuestionnaireDashboard: React.FC<Props> = ({
    jobQuestionId,
    questions,
    jobTitle,
    companyName,
}) => {
    const [loading, setLoading] = useState(true);
    const [progressLoaded, setProgressLoaded] = useState(0);
    const [progressTotal, setProgressTotal] = useState(0);
    const [error, setError] = useState<string | null>(null);
    const [reports, setReports] = useState<ReportShape[]>([]);
    const [completedCount, setCompletedCount] = useState(0);
    const [allCandidatesCount, setAllCandidatesCount] = useState(0);
    const [isDownloading, setIsDownloading] = useState(false);
    const [downloadError, setDownloadError] = useState<string | null>(null);
    const lastJobIdRef = useRef<string | null>(null);

    useEffect(() => {
        if (!jobQuestionId) return;
        if (lastJobIdRef.current === jobQuestionId) return;
        lastJobIdRef.current = jobQuestionId;

        const ctrl = { cancelled: false };
        const run = async () => {
            setLoading(true);
            setError(null);
            setReports([]);
            setProgressLoaded(0);
            setProgressTotal(0);
            try {
                const candRes = await getAllCandidate(jobQuestionId);
                const list = ((candRes?.data?.data || []) as Candidate[]) || [];
                setAllCandidatesCount(list.length);
                const completed = list.filter(
                    (c) => c.interview_status === "COMPLETED" && c.report_id,
                );
                setCompletedCount(completed.length);
                setProgressTotal(completed.length);
                if (completed.length === 0) {
                    setLoading(false);
                    return;
                }
                const out: ReportShape[] = [];
                let done = 0;
                const concurrency = 4;
                let cursor = 0;
                const worker = async () => {
                    while (!ctrl.cancelled) {
                        const myIndex = cursor;
                        cursor += 1;
                        if (myIndex >= completed.length) return;
                        const c = completed[myIndex];
                        try {
                            const r = await getReportById(c.report_id!);
                            if (r?.data?.is_success) {
                                out.push(r.data.data as ReportShape);
                            }
                        } catch {
                            // skip silently — partial dashboard is still useful
                        } finally {
                            done += 1;
                            if (!ctrl.cancelled) setProgressLoaded(done);
                        }
                    }
                };
                await Promise.all(
                    Array.from({ length: Math.min(concurrency, completed.length) }, () => worker()),
                );
                if (!ctrl.cancelled) setReports(out);
            } catch (e: any) {
                if (!ctrl.cancelled) setError(e?.message || "โหลดรายงานไม่สำเร็จ");
            } finally {
                if (!ctrl.cancelled) setLoading(false);
            }
        };
        void run();

        return () => {
            ctrl.cancelled = true;
        };
    }, [jobQuestionId]);

    const aggregates = useMemo(() => {
        const map = new Map<string, PerQuestionAggregate>();
        for (const q of questions) {
            const choices = q.choices || [];
            const isMC = isMultipleChoiceLike(q.question_type);
            const isCb = isCheckboxQuestion(q);
            map.set(q.question_id || "", {
                answered: 0,
                totalEvaluated: reports.length,
                choiceCounts: isMC ? Array(choices.length).fill(0) : undefined,
                correctCount: q.question_type === MULTIPLE_CHOICE ? 0 : undefined,
                totalVotes: isCb ? 0 : undefined,
                averageCharLength: isMC ? undefined : 0,
                answerSamples: isMC ? undefined : [],
            });
        }

        const totals = new Map<string, { answered: number; sumChars: number }>();
        for (const q of questions) {
            totals.set(q.question_id || "", { answered: 0, sumChars: 0 });
        }

        for (const rep of reports) {
            const logs = rep?.transcript?.logs || [];
            const answers = rep?.transcript?.answers || [];
            for (const log of logs) {
                const qid = String(log.question_id || "");
                const agg = map.get(qid);
                const tot = totals.get(qid);
                if (!agg || !tot) continue;
                const q = questions.find((x) => (x.question_id || "") === qid);
                if (!q) continue;
                const isCb = isCheckboxQuestion({
                    question_type: q.question_type,
                    answer_type: q.answer_type ?? log.answer_type ?? null,
                    ui_variant: q.ui_variant ?? log.ui_variant ?? null,
                });
                if (isMultipleChoiceLike(q.question_type)) {
                    if (isCb) {
                        const idxs = readChoiceIndexesFromLog(log);
                        if (idxs.length === 0) continue;
                        for (const idx of idxs) {
                            if (agg.choiceCounts && idx >= 0 && idx < agg.choiceCounts.length) {
                                agg.choiceCounts[idx] += 1;
                                agg.totalVotes = (agg.totalVotes ?? 0) + 1;
                            }
                        }
                        tot.answered += 1;
                    } else {
                        const idx = readChoiceIndexFromLog(log);
                        if (idx === null) continue;
                        if (agg.choiceCounts && idx >= 0 && idx < agg.choiceCounts.length) {
                            agg.choiceCounts[idx] += 1;
                        }
                        tot.answered += 1;
                        if (
                            q.question_type === MULTIPLE_CHOICE &&
                            Number(q.answer_choice?.answer_index) === idx
                        ) {
                            agg.correctCount = (agg.correctCount ?? 0) + 1;
                        }
                    }
                } else {
                    const text = readFreeTextAnswer(log, answers);
                    if (!text) continue;
                    tot.answered += 1;
                    tot.sumChars += text.length;
                    if (agg.answerSamples && agg.answerSamples.length < MAX_SAMPLES) {
                        agg.answerSamples.push(text);
                    }
                }
            }
        }

        for (const q of questions) {
            const qid = q.question_id || "";
            const agg = map.get(qid)!;
            const tot = totals.get(qid)!;
            agg.answered = tot.answered;
            if (!isMultipleChoiceLike(q.question_type)) {
                agg.averageCharLength = tot.answered > 0 ? Math.round(tot.sumChars / tot.answered) : 0;
            }
        }
        return map;
    }, [reports, questions]);

    const overall = useMemo(() => {
        const totalQuestionsAnswered = Array.from(aggregates.values()).reduce(
            (a, b) => a + b.answered,
            0,
        );
        return {
            reportsLoaded: reports.length,
            totalQuestionsAnswered,
            avgAnsweredPerCandidate:
                reports.length > 0
                    ? Math.round((totalQuestionsAnswered / reports.length) * 10) / 10
                    : 0,
        };
    }, [aggregates, reports.length]);

    /**
     * Build the PDF payload from the same aggregates the on-screen dashboard
     * uses, then stream a Blob through pdf().toBlob() and trigger a download.
     * We intentionally rebuild the rows only on click (not via useMemo) so the
     * PDF library's lazy-load of the Thai font only happens when the admin
     * actually requests a PDF.
     */
    const handleDownloadPdf = async () => {
        if (isDownloading) return;
        setDownloadError(null);
        setIsDownloading(true);
        try {
            const rows: QuestionPdfRow[] = questions.map((q) => {
                const agg = aggregates.get(q.question_id || "") || {
                    answered: 0,
                    totalEvaluated: reports.length,
                };
                return {
                    question: q,
                    answered: agg.answered,
                    totalEvaluated: agg.totalEvaluated,
                    choiceCounts: agg.choiceCounts,
                    correctCount: agg.correctCount,
                    totalVotes: agg.totalVotes,
                    averageCharLength: agg.averageCharLength,
                    answerSamples: agg.answerSamples,
                };
            });

            const data: DashboardPdfData = {
                jobTitle: String(jobTitle || "ชุดคำถามทั่วไป").trim(),
                companyName: companyName ? String(companyName).trim() : undefined,
                allCandidatesCount,
                completedCount,
                reportsLoaded: overall.reportsLoaded,
                avgAnsweredPerCandidate: overall.avgAnsweredPerCandidate,
                totalQuestions: questions.length,
                rows,
                generatedAt: new Date().toLocaleString("th-TH", {
                    dateStyle: "long",
                    timeStyle: "short",
                }),
            };

            const blob = await pdf(<GeneralQuestionnaireDashboardPDF data={data} />).toBlob();
            const url = URL.createObjectURL(blob);
            const safeTitle = (jobTitle || "general-questionnaire-dashboard")
                .replace(/[\\/:*?"<>|]+/g, "")
                .trim() || "general-questionnaire-dashboard";
            const a = document.createElement("a");
            a.href = url;
            a.download = `${safeTitle} - ${new Date().toISOString().slice(0, 10)}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            // Free the object URL after the download has been triggered.
            setTimeout(() => URL.revokeObjectURL(url), 1000);
        } catch (err: any) {
            console.error("Download dashboard PDF failed:", err);
            setDownloadError(err?.message || "ไม่สามารถสร้างไฟล์ PDF ได้");
        } finally {
            setIsDownloading(false);
        }
    };

    if (loading) {
        return (
            <div className="gqd-wrap">
                <div className="gqd-loading">
                    <CircularProgress size={28} sx={{ color: "#4A25E1" }} />
                    <span>
                        กำลังโหลดรายงาน {progressLoaded}/{progressTotal} ฉบับ…
                    </span>
                    {progressTotal > 0 && (
                        <Box sx={{ width: 280 }}>
                            <LinearProgress
                                variant="determinate"
                                value={(progressLoaded / Math.max(1, progressTotal)) * 100}
                            />
                        </Box>
                    )}
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="gqd-wrap">
                <Alert severity="error">{error}</Alert>
            </div>
        );
    }

    if (completedCount === 0) {
        return (
            <div className="gqd-wrap">
                <Alert severity="info">
                    ยังไม่มีผู้สมัครที่ทำแบบสอบถามเสร็จ — Dashboard จะปรากฏหลังจากมีผู้สมัครส่งคำตอบและรายงานพร้อมแล้ว
                </Alert>
            </div>
        );
    }

    return (
        <div className="gqd-wrap">
            {/* Toolbar */}
            <div className="gqd-toolbar">
                <Button
                    variant="contained"
                    size="small"
                    startIcon={isDownloading ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : <DownloadIcon />}
                    onClick={handleDownloadPdf}
                    disabled={isDownloading}
                    sx={{
                        backgroundColor: "#4A25E1",
                        textTransform: "none",
                        fontWeight: 600,
                        boxShadow: "none",
                        "&:hover": { backgroundColor: "#3b1bb8", boxShadow: "none" },
                    }}
                >
                    {isDownloading ? "กำลังสร้าง PDF…" : "ดาวน์โหลด PDF"}
                </Button>
            </div>
            {downloadError && (
                <Alert severity="error" sx={{ mb: 2 }}>{downloadError}</Alert>
            )}

            {/* Summary strip */}
            <div className="gqd-summary">
                <div className="gqd-summary-card">
                    <span className="gqd-summary-label">รายงานที่นำมาวิเคราะห์</span>
                    <span className="gqd-summary-value">
                        {overall.reportsLoaded}
                        <small> / {completedCount} เสร็จสิ้น</small>
                    </span>
                </div>
                <div className="gqd-summary-card">
                    <span className="gqd-summary-label">ผู้สมัครทั้งหมด</span>
                    <span className="gqd-summary-value">{allCandidatesCount}</span>
                </div>
                <div className="gqd-summary-card">
                    <span className="gqd-summary-label">คำถามในชุด</span>
                    <span className="gqd-summary-value">{questions.length}</span>
                </div>
                <div className="gqd-summary-card">
                    <span className="gqd-summary-label">เฉลี่ยข้อที่ตอบ / คน</span>
                    <span className="gqd-summary-value">{overall.avgAnsweredPerCandidate}</span>
                </div>
            </div>

            <div className="gqd-question-list">
                {questions.map((q, qi) => {
                    const agg = aggregates.get(q.question_id || "");
                    if (!agg) return null;
                    const isMC = isMultipleChoiceLike(q.question_type);
                    const isCb = isCheckboxQuestion(q);
                    /**
                     * เปอร์เซ็นต์: คิดเทียบกับจำนวน "คน" ที่ตอบข้อนี้ (agg.answered)
                     * - single-select: 1 คน = 1 vote → รวมทุก choice เท่ากับ answered
                     * - checkbox: 1 คนเลือกได้หลาย choice → แต่ละ choice = % ของคนที่เลือก
                     * fallback ถ้ายังไม่มีคนตอบเลย ใช้ totalEvaluated เพื่อกัน /0
                     */
                    const denomForPct = Math.max(1, agg.answered || agg.totalEvaluated);
                    const correctIdx =
                        q.question_type === MULTIPLE_CHOICE
                            ? Number(q.answer_choice?.answer_index)
                            : -1;
                    const typeChipLabel = isCb
                        ? "เลือกตอบ (หลายข้อได้)"
                        : q.question_type === MULTIPLE_CHOICE
                          ? "เลือกตอบ (มีเฉลย)"
                          : q.question_type === MULTIPLE_CHOICE_NO_ANSWER
                            ? "เลือกตอบ"
                            : "ตอบด้วยข้อความ/เสียง";
                    const typeChipBg = isCb
                        ? "#ddd6fe"
                        : q.question_type === MULTIPLE_CHOICE
                          ? "#ede9fe"
                          : q.question_type === MULTIPLE_CHOICE_NO_ANSWER
                            ? "#e0f2fe"
                            : "#fef3c7";
                    return (
                        <div className="gqd-question-card" key={q.question_id || qi}>
                            <div className="gqd-question-header">
                                <span className="gqd-question-number">Q{qi + 1}</span>
                                <div className="gqd-question-text">
                                    <span className="gqd-question-th">{q.question.th}</span>
                                    {q.question.en && (
                                        <span className="gqd-question-en">{q.question.en}</span>
                                    )}
                                </div>
                                <div className="gqd-question-meta">
                                    <Chip
                                        size="small"
                                        label={typeChipLabel}
                                        sx={{
                                            backgroundColor: typeChipBg,
                                            color: "#1e293b",
                                            fontWeight: 600,
                                        }}
                                    />
                                    <span className="gqd-question-answered">
                                        ตอบแล้ว {agg.answered}/{agg.totalEvaluated}
                                        {isCb && (agg.totalVotes ?? 0) > 0 && (
                                            <> · {agg.totalVotes} vote</>
                                        )}
                                    </span>
                                </div>
                            </div>

                            {isMC ? (
                                <div className="gqd-bars">
                                    {(q.choices || []).map((choice, ci) => {
                                        const count = agg.choiceCounts?.[ci] ?? 0;
                                        const pct = Math.round((count / denomForPct) * 100);
                                        const isCorrect = !isCb && ci === correctIdx;
                                        return (
                                            <div className="gqd-bar-row" key={ci}>
                                                <div className="gqd-bar-label">
                                                    <span className="gqd-bar-index">{ci + 1}.</span>
                                                    <span className="gqd-bar-text">
                                                        {choiceLabel(choice, ci)}
                                                    </span>
                                                    {isCorrect && (
                                                        <span className="gqd-correct-flag">
                                                            ✓ เฉลย
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="gqd-bar-track">
                                                    <div
                                                        className="gqd-bar-fill"
                                                        style={{
                                                            width: `${pct}%`,
                                                            backgroundColor: isCorrect
                                                                ? "#10b981"
                                                                : PALETTE[ci % PALETTE.length],
                                                        }}
                                                    />
                                                </div>
                                                <div className="gqd-bar-value">
                                                    <strong>{count}</strong>
                                                    <span>{pct}%</span>
                                                </div>
                                            </div>
                                        );
                                    })}
                                    {q.question_type === MULTIPLE_CHOICE &&
                                        agg.answered > 0 && (
                                            <div className="gqd-correct-summary">
                                                ตอบถูก {agg.correctCount ?? 0}/{agg.answered} (
                                                {Math.round(
                                                    ((agg.correctCount ?? 0) / agg.answered) * 100,
                                                )}
                                                %)
                                            </div>
                                        )}
                                </div>
                            ) : (
                                <div className="gqd-text-summary">
                                    <div className="gqd-text-stats">
                                        <Typography variant="body2" sx={{ color: "#475569" }}>
                                            จำนวนผู้ที่ตอบ:{" "}
                                            <strong>{agg.answered}</strong> /{" "}
                                            {agg.totalEvaluated} (
                                            {Math.round((agg.answered / Math.max(1, agg.totalEvaluated)) * 100)}%)
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: "#475569" }}>
                                            ความยาวเฉลี่ย:{" "}
                                            <strong>{agg.averageCharLength ?? 0}</strong> ตัวอักษร
                                        </Typography>
                                    </div>
                                    {(agg.answerSamples?.length ?? 0) > 0 ? (
                                        <ul className="gqd-text-samples">
                                            {(agg.answerSamples || []).map((s, i) => (
                                                <li key={i}>{s}</li>
                                            ))}
                                        </ul>
                                    ) : (
                                        <Typography
                                            variant="body2"
                                            sx={{ color: "#94a3b8", fontStyle: "italic" }}
                                        >
                                            ยังไม่มีข้อความตัวอย่าง
                                        </Typography>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default GeneralQuestionnaireDashboard;
